/**
 * Per-zone soil history: a hand-rolled SVG chart. No chart library — geometry
 * is computed here and drawn as plain SVG so the whole thing scales
 * responsively inside the modal. A segmented control switches between three
 * views:
 *   - Deficit  — the modeled soil deficit against its watering trigger, the
 *     sensor-derived deficit, and daily rain/irrigation depth bars.
 *   - Moisture — raw sensor moisture % with the calibration bounds ("Water at"
 *     / "Full"), rain/watered day markers, and detected field-capacity
 *     plateaus (computed client-side from the same runs + rain + readings).
 *   - Table    — the underlying daily numbers.
 */

import { useEffect, useRef, useState } from 'react';
import type { RunRecord, SoilSnapshot, WettingEvent } from '@irrigation/core';
import {
  allowedDepletionIn,
  deficitFromMoisture,
  detectFieldCapacityObservations,
  RAIN_EVENT_MIN_IN,
} from '@irrigation/core';
import type { ZoneWithSoil } from '../api.js';
import { EmptyState } from './ui.js';
import { friendlyDate, toIsoDate } from '../lib/format.js';

interface Props {
  zone: ZoneWithSoil;
  snapshots: SoilSnapshot[];
  readings: { at: string; moisturePct: number }[];
  runs: RunRecord[];
  /** Which view to open on (defaults to deficit). */
  defaultMode?: Mode;
  /** Suggested bounds to overlay against the current ones, in moisture mode. */
  suggestedLowPct?: number;
  suggestedHighPct?: number;
}

type Mode = 'deficit' | 'moisture' | 'table';

/**
 * Chart geometry, chosen from the rendered width.
 *
 * A single 640-unit viewBox looked fine on a desktop and was unreadable on a
 * phone: inside a modal at 390px the drawing is ~314px wide, so it scaled to
 * 0.49x and the 10-unit axis labels landed at about 4.9px. The narrow variant
 * uses a smaller box (less downscaling), bigger type, and half as many days so
 * each slot is wide enough to hit.
 */
function geometryFor(narrow: boolean) {
  const VB_W = narrow ? 340 : 640;
  const VB_H = narrow ? 250 : 240;
  const PAD_L = narrow ? 30 : 34;
  const PAD_R = narrow ? 6 : 8;
  const PAD_T = 8;
  const PAD_B = narrow ? 26 : 18;
  return {
    VB_W,
    VB_H,
    PAD_L,
    PAD_R,
    PAD_T,
    PAD_B,
    plotW: VB_W - PAD_L - PAD_R,
    plotH: VB_H - PAD_T - PAD_B,
    baseY: VB_H - PAD_B,
    /**
     * Axis/label size in viewBox units. Rendered size differs by variant because
     * each downscales differently inside the 560px-max modal: ~11.9px narrow
     * (13 × 0.92), ~8px wide (10 × 0.80). The wide value is at the floor of
     * legibility — if that modal ever gets narrower, raise this with it.
     */
    fs: narrow ? 13 : 10,
    dayCount: narrow ? 14 : 30,
  };
}

interface Hover {
  day: number;
  left: number;
  top: number;
}

/** A colored legend/tooltip mark: a short line, a chip, or a round dot. */
function Swatch({ kind, color }: { kind: 'line' | 'rect' | 'dot'; color: string }) {
  if (kind === 'line') {
    return <span style={{ display: 'inline-block', width: 14, height: 0, borderTop: `2px solid ${color}`, borderRadius: 1 }} />;
  }
  if (kind === 'dot') {
    return (
      <span
        style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: color, boxShadow: '0 0 0 1.5px var(--panel)' }}
      />
    );
  }
  return <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: color }} />;
}

const monthDay = (iso: string): string => {
  const [, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}`;
};

export function SoilHistoryChart({ zone, snapshots, readings, runs, defaultMode, suggestedLowPct, suggestedHighPct }: Props) {
  const hasSensor = Boolean(zone.moistureSensorId);
  const [mode, setMode] = useState<Mode>(defaultMode ?? 'deficit');
  const [hover, setHover] = useState<Hover | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  if (snapshots.length === 0 && readings.length === 0) {
    return (
      <EmptyState icon="📈" title="No history yet">
        History accumulates as the planner runs — check back in a few days.
      </EmptyState>
    );
  }

  // Moisture mode is only meaningful with a sensor; fall back if it's off.
  const effMode: Mode = mode === 'moisture' && !hasSensor ? 'deficit' : mode;

  // Measure the drawing area rather than the viewport: this chart lives inside
  // a modal, so the window width says little about how much room it has.
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect.width ?? 0;
      if (w > 0) setNarrow(w < 480);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { VB_W, VB_H, PAD_L, PAD_R, PAD_T, plotW, plotH, baseY, fs, dayCount } = geometryFor(narrow);

  // Day domain: the last 30 calendar days ending today, oldest first.
  const days: string[] = [];
  for (let i = dayCount - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(toIsoDate(d));
  }

  const trigger = allowedDepletionIn(zone);

  // Model deficit + rain, keyed by day.
  const modelByDate = new Map<string, number>();
  const rainByDate = new Map<string, number>();
  for (const s of snapshots) {
    modelByDate.set(s.date, s.deficitIn);
    rainByDate.set(s.date, s.rainIn);
  }

  // Net water into the *model* per day: completed, non-dry-run zone runs.
  const waterByDate = new Map<string, number>();
  for (const r of runs) {
    if (r.zoneId !== zone.id || r.status !== 'COMPLETED' || r.dryRun) continue;
    waterByDate.set(r.plan.date, (waterByDate.get(r.plan.date) ?? 0) + r.plan.netDepthIn);
  }

  // Sensor readings → deficit points (fractional x within their day), plus the
  // last reading per day for the tooltip/table. Also keep the raw % per day and
  // fractional-x % points for the moisture view.
  const sensorPoints: { day: number; frac: number; deficit: number }[] = [];
  const moisturePoints: { day: number; frac: number; pct: number }[] = [];
  const sensorLastByDate = new Map<string, { pct: number; deficit: number }>();
  for (const rd of readings) {
    const date = rd.at.slice(0, 10);
    const dayIndex = days.indexOf(date);
    if (dayIndex < 0) continue;
    const hour = Number(rd.at.slice(11, 13));
    const frac = (Number.isFinite(hour) ? hour : 0) / 24;
    moisturePoints.push({ day: dayIndex, frac, pct: rd.moisturePct });
    const deficit = deficitFromMoisture(zone, rd.moisturePct);
    if (deficit === undefined) continue;
    sensorPoints.push({ day: dayIndex, frac, deficit });
    sensorLastByDate.set(date, { pct: rd.moisturePct, deficit });
  }

  // Client-side field-capacity plateaus: build wetting events from the same
  // runs (irrigation) + rain days already in hand, then detect plateaus in the
  // readings. Core is pure, so this mirrors exactly what the server computes.
  const plateauByDate = new Map<string, number>();
  const plateauPoints: { day: number; frac: number; pct: number }[] = [];
  if (hasSensor) {
    const events: WettingEvent[] = [];
    for (const r of runs) {
      if (r.zoneId !== zone.id || r.status !== 'COMPLETED' || r.dryRun) continue;
      const start = r.actualStart ?? r.scheduledAt;
      const end =
        r.actualEnd ?? new Date(Date.parse(start) + r.plan.totalRuntimeMinutes * 60_000).toISOString();
      events.push({ start, end, source: 'IRRIGATION', runNetDepthIn: r.plan.netDepthIn });
    }
    for (const s of snapshots) {
      if (s.rainIn >= RAIN_EVENT_MIN_IN) {
        // Day-granular: local day start/end (no offset → parsed as local time).
        events.push({ start: `${s.date}T00:00:00`, end: `${s.date}T23:59:59`, source: 'RAIN' });
      }
    }
    const observations = detectFieldCapacityObservations(events, readings, zone.soilType, new Date());
    for (const o of observations) {
      const date = o.plateauAt.slice(0, 10);
      const dayIndex = days.indexOf(date);
      if (dayIndex < 0) continue;
      const hour = Number(o.plateauAt.slice(11, 13));
      const frac = (Number.isFinite(hour) ? hour : 0) / 24;
      plateauPoints.push({ day: dayIndex, frac, pct: o.plateauPct });
      plateauByDate.set(date, o.plateauPct);
    }
  }

  // ---- Deficit-mode y axis: headroom above the trigger, never below data. ----
  const values = [trigger * 1.3];
  for (const s of snapshots) values.push(s.deficitIn, s.rainIn);
  for (const w of waterByDate.values()) values.push(w);
  for (const p of sensorPoints) values.push(p.deficit);
  const yMax = Math.max(0.25, Math.ceil(Math.max(0.25, ...values) / 0.25) * 0.25);

  const step = Math.max(0.25, Math.ceil(yMax / 4 / 0.25) * 0.25);
  const gridVals: number[] = [];
  for (let v = step; v <= yMax + 1e-9; v += step) gridVals.push(Math.round(v * 100) / 100);

  // ---- Moisture-mode y axis: a padded window around the data + bounds. ----
  const low = zone.moistureLowPct;
  const high = zone.moistureHighPct;
  const mVals: number[] = [];
  for (const p of moisturePoints) mVals.push(p.pct);
  for (const p of plateauPoints) mVals.push(p.pct);
  if (low !== undefined) mVals.push(low);
  if (high !== undefined) mVals.push(high);
  if (suggestedLowPct !== undefined) mVals.push(suggestedLowPct);
  if (suggestedHighPct !== undefined) mVals.push(suggestedHighPct);
  let mMin = mVals.length ? Math.max(0, Math.floor((Math.min(...mVals) - 3) / 5) * 5) : 0;
  let mMax = mVals.length ? Math.min(100, Math.ceil((Math.max(...mVals) + 3) / 5) * 5) : 50;
  if (mMax - mMin < 10) mMax = Math.min(100, mMin + 10);
  const mStep = Math.max(5, Math.round((mMax - mMin) / 4 / 5) * 5);
  const mGrid: number[] = [];
  for (let v = mMin; v <= mMax + 1e-9; v += mStep) mGrid.push(v);

  const slotW = plotW / days.length;
  const xForDay = (i: number) => PAD_L + (i + 0.5) * slotW;
  const xForFrac = (i: number, frac: number) => PAD_L + (i + frac) * slotW;
  const yFor = (v: number) => baseY - (v / yMax) * plotH;
  const yForM = (v: number) => baseY - ((v - mMin) / (mMax - mMin)) * plotH;

  const barGap = 2;
  const barW = Math.min(10, (slotW - barGap) / 2);

  // Model line, breaking the path across missing days.
  let modelPath = '';
  let pen = false;
  days.forEach((date, i) => {
    const v = modelByDate.get(date);
    if (v === undefined) {
      pen = false;
      return;
    }
    modelPath += `${pen ? 'L' : 'M'}${xForDay(i).toFixed(1)} ${yFor(v).toFixed(1)} `;
    pen = true;
  });

  let modelEndIdx = -1;
  for (let i = days.length - 1; i >= 0; i--) {
    if (modelByDate.has(days[i]!)) {
      modelEndIdx = i;
      break;
    }
  }

  // Sensor line (deficit mode), connecting its points in time order.
  let sensorPath = '';
  sensorPoints.forEach((p, i) => {
    sensorPath += `${i === 0 ? 'M' : 'L'}${xForFrac(p.day, p.frac).toFixed(1)} ${yFor(p.deficit).toFixed(1)} `;
  });
  const sensorEnd = sensorPoints[sensorPoints.length - 1];

  // Sensor line (moisture mode), same points in raw %.
  let moisturePath = '';
  moisturePoints.forEach((p, i) => {
    moisturePath += `${i === 0 ? 'M' : 'L'}${xForFrac(p.day, p.frac).toFixed(1)} ${yForM(p.pct).toFixed(1)} `;
  });
  const moistureEnd = moisturePoints[moisturePoints.length - 1];

  const triggerY = yFor(trigger);

  // Direct end labels; nudge the sensor label clear of the model one.
  const modelEndY = modelEndIdx >= 0 ? yFor(modelByDate.get(days[modelEndIdx]!)!) : 0;
  const modelLabelY = modelEndY - 6;
  let sensorLabelY = sensorEnd ? yFor(sensorEnd.deficit) - 6 : 0;
  if (modelEndIdx >= 0 && sensorEnd && Math.abs(sensorLabelY - modelLabelY) < 12) {
    sensorLabelY = modelLabelY + 12;
  }

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const svg = svgRef.current;
    const wrap = wrapRef.current;
    if (!svg || !wrap) return;
    const svgRect = svg.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const plotLeftPx = svgRect.left + (PAD_L / VB_W) * svgRect.width;
    const plotWidthPx = (plotW / VB_W) * svgRect.width;
    const frac = (e.clientX - plotLeftPx) / plotWidthPx;
    const day = Math.max(0, Math.min(days.length - 1, Math.round(frac * (days.length - 1))));
    let left = e.clientX - wrapRect.left + 12;
    left = Math.max(0, Math.min(left, wrapRect.width - 160));
    const top = Math.max(0, e.clientY - wrapRect.top + 12);
    setHover({ day, left, top });
  };

  /** What the cursor is actually sitting on, for assistive tech. */
  const valueTextFor = (di: number): string => {
    const date = days[di];
    if (!date) return '';
    const rows = tooltipRows(di);
    return rows.length > 0 ? `${friendlyDate(date)}: ${rows.map((r) => `${r.name} ${r.val}`).join(', ')}` : friendlyDate(date);
  };

  const onKey = (e: React.KeyboardEvent<SVGRectElement>) => {
    if (e.key === 'Escape') {
      setHover(null);
      return;
    }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const cur = hover?.day ?? days.length - 1;
    const day = Math.max(0, Math.min(days.length - 1, cur + (e.key === 'ArrowRight' ? 1 : -1)));
    const svg = svgRef.current;
    const wrap = wrapRef.current;
    if (!svg || !wrap) {
      setHover({ day, left: 0, top: 0 });
      return;
    }
    const svgRect = svg.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    let left = svgRect.left - wrapRect.left + (xForDay(day) / VB_W) * svgRect.width + 8;
    left = Math.max(0, Math.min(left, wrapRect.width - 160));
    const top = Math.max(0, svgRect.top - wrapRect.top + 8);
    setHover({ day, left, top });
  };

  const tableRows = days
    .filter(
      (date) =>
        modelByDate.has(date) ||
        sensorLastByDate.has(date) ||
        (rainByDate.get(date) ?? 0) > 0 ||
        (waterByDate.get(date) ?? 0) > 0,
    )
    .reverse();

  interface Row {
    key: string;
    kind: 'line' | 'rect' | 'dot';
    color: string;
    name: string;
    val: string;
  }

  const tooltipRows = (di: number): Row[] => {
    const date = days[di]!;
    const rows: Row[] = [];
    if (effMode === 'moisture') {
      const s = sensorLastByDate.get(date);
      if (s) rows.push({ key: 'sensor', kind: 'line', color: 'var(--viz-sensor)', name: 'Sensor', val: `${s.pct}%` });
      const pl = plateauByDate.get(date);
      if (pl !== undefined)
        rows.push({ key: 'plateau', kind: 'dot', color: 'var(--accent)', name: 'Field capacity', val: `${pl}%` });
      const r = rainByDate.get(date) ?? 0;
      if (r > 0) rows.push({ key: 'rain', kind: 'rect', color: 'var(--viz-rain)', name: 'Rain', val: `${r.toFixed(2)} in` });
      const w = waterByDate.get(date) ?? 0;
      if (w > 0) rows.push({ key: 'water', kind: 'rect', color: 'var(--viz-water)', name: 'Watered', val: `${w.toFixed(2)} in` });
      return rows;
    }
    const m = modelByDate.get(date);
    if (m !== undefined) rows.push({ key: 'model', kind: 'line', color: 'var(--viz-model)', name: 'Model deficit', val: `${m.toFixed(2)} in` });
    const r = rainByDate.get(date) ?? 0;
    if (r > 0) rows.push({ key: 'rain', kind: 'rect', color: 'var(--viz-rain)', name: 'Rain', val: `${r.toFixed(2)} in` });
    const w = waterByDate.get(date) ?? 0;
    if (w > 0) rows.push({ key: 'water', kind: 'rect', color: 'var(--viz-water)', name: 'Watered', val: `${w.toFixed(2)} in` });
    const s = sensorLastByDate.get(date);
    if (s) rows.push({ key: 'sensor', kind: 'line', color: 'var(--viz-sensor)', name: 'Sensor', val: `${s.deficit.toFixed(2)} in (${s.pct}%)` });
    return rows;
  };

  const seg: { key: Mode; label: string }[] = [
    { key: 'deficit', label: 'Deficit' },
    ...(hasSensor ? [{ key: 'moisture' as Mode, label: 'Moisture' }] : []),
    { key: 'table', label: 'Table' },
  ];

  const hoverLine = hover && effMode !== 'table' && (
    <line x1={xForDay(hover.day)} x2={xForDay(hover.day)} y1={PAD_T} y2={baseY} stroke="var(--border-strong)" strokeWidth={1} />
  );

  const overlay = (
    <rect
      x={PAD_L}
      y={PAD_T}
      width={plotW}
      height={plotH}
      fill="transparent"
      tabIndex={0}
      role="slider"
      aria-label="Inspect soil history by day; use left and right arrow keys"
      aria-valuemin={0}
      aria-valuemax={days.length - 1}
      aria-valuenow={hover?.day ?? days.length - 1}
      aria-valuetext={valueTextFor(hover?.day ?? days.length - 1)}
      style={{ outline: 'none', cursor: 'crosshair' }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        onMove(e);
      }}
      onPointerMove={(e) => {
        // Mouse hovers; a finger has to be down to mean anything.
        if (e.pointerType === 'mouse' || e.buttons > 0 || e.pressure > 0) onMove(e);
      }}
      onPointerUp={(e) => e.currentTarget.releasePointerCapture?.(e.pointerId)}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') setHover(null);
      }}
      onKeyDown={onKey}
    />
  );

  return (
    <div className="viz-wrap" ref={wrapRef}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
        <div className="viz-legend">
          {effMode === 'moisture' ? (
            <>
              <span className="key">
                <Swatch kind="line" color="var(--viz-sensor)" /> Sensor
              </span>
              <span className="key">
                <Swatch kind="dot" color="var(--accent)" /> Field capacity
              </span>
              <span className="key">
                <Swatch kind="rect" color="var(--viz-rain)" /> Rain
              </span>
              <span className="key">
                <Swatch kind="rect" color="var(--viz-water)" /> Watered
              </span>
            </>
          ) : (
            <>
              <span className="key">
                <Swatch kind="line" color="var(--viz-model)" /> Model deficit
              </span>
              <span className="key">
                <Swatch kind="rect" color="var(--viz-rain)" /> Rain
              </span>
              <span className="key">
                <Swatch kind="rect" color="var(--viz-water)" /> Watered
              </span>
              {hasSensor && (
                <span className="key">
                  <Swatch kind="line" color="var(--viz-sensor)" /> Sensor
                </span>
              )}
            </>
          )}
        </div>
        <div className="viz-seg" role="group" aria-label="Chart view">
          {seg.map((s) => (
            <button
              key={s.key}
              type="button"
              className={effMode === s.key ? 'on' : ''}
              aria-pressed={effMode === s.key}
              onClick={() => setMode(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {effMode === 'table' ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="viz-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Model deficit (in)</th>
                <th>Sensor (in)</th>
                <th>Rain (in)</th>
                <th>Watered (in)</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((date) => {
                const m = modelByDate.get(date);
                const s = sensorLastByDate.get(date);
                const r = rainByDate.get(date) ?? 0;
                const w = waterByDate.get(date) ?? 0;
                return (
                  <tr key={date}>
                    <td>{friendlyDate(date)}</td>
                    <td>{m !== undefined ? m.toFixed(2) : '—'}</td>
                    <td>{s ? s.deficit.toFixed(2) : '—'}</td>
                    <td>{r > 0 ? r.toFixed(2) : '—'}</td>
                    <td>{w > 0 ? w.toFixed(2) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : effMode === 'moisture' ? (
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          role="img"
          aria-label={`Sensor moisture history for ${zone.name}`}
          // Must sit on the <svg>, not the overlay <rect> inside it: Chrome
          // ignores touch-action on SVG child elements for gesture
          // arbitration, so a drag was reclaimed for scrolling and the pointer
          // stream ended in pointercancel (a tap worked, a scrub did not).
          style={{ touchAction: 'none' }}
        >
          {mGrid.map((v) => {
            const y = yForM(v);
            return (
              <g key={v}>
                <line x1={PAD_L} x2={VB_W - PAD_R} y1={y} y2={y} stroke="var(--border)" strokeWidth={1} />
                <text x={PAD_L - 4} y={y} dy={3} fontSize={fs} fill="var(--faint)" textAnchor="end">
                  {v}%
                </text>
              </g>
            );
          })}

          {days.map((date, i) =>
            i % 5 === 0 ? (
              <text key={date} x={xForDay(i)} y={VB_H - 6} fontSize={fs} fill="var(--faint)" textAnchor="middle">
                {monthDay(date)}
              </text>
            ) : null,
          )}

          {/* Rain/watered day markers along the baseline (chips, not depth bars). */}
          {days.map((date, i) => {
            const center = xForDay(i);
            const r = rainByDate.get(date) ?? 0;
            const w = waterByDate.get(date) ?? 0;
            return (
              <g key={date}>
                {r > 0 && <rect x={center - barGap / 2 - barW} y={baseY - 5} width={barW} height={5} rx={2} fill="var(--viz-rain)" />}
                {w > 0 && <rect x={center + barGap / 2} y={baseY - 5} width={barW} height={5} rx={2} fill="var(--viz-water)" />}
              </g>
            );
          })}

          {/* Calibration bounds. */}
          {high !== undefined && high >= mMin && high <= mMax && (
            <>
              <line x1={PAD_L} x2={VB_W - PAD_R} y1={yForM(high)} y2={yForM(high)} stroke="var(--muted)" strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
              <text x={VB_W - PAD_R} y={yForM(high)} dy={-3} fontSize={fs} fill="var(--muted)" textAnchor="end">
                Full {high}%
              </text>
            </>
          )}
          {low !== undefined && low >= mMin && low <= mMax && (
            <>
              <line x1={PAD_L} x2={VB_W - PAD_R} y1={yForM(low)} y2={yForM(low)} stroke="var(--muted)" strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
              <text x={VB_W - PAD_R} y={yForM(low)} dy={-3} fontSize={fs} fill="var(--muted)" textAnchor="end">
                Water at {low}%
              </text>
            </>
          )}

          {/* Suggested bounds from an on-demand calibration check, drawn against
              the current ones so the gap is visible. */}
          {suggestedHighPct !== undefined && suggestedHighPct >= mMin && suggestedHighPct <= mMax && (
            <>
              <line x1={PAD_L} x2={VB_W - PAD_R} y1={yForM(suggestedHighPct)} y2={yForM(suggestedHighPct)} stroke="var(--accent)" strokeWidth={1.5} strokeDasharray="2 3" />
              <text x={PAD_L} y={yForM(suggestedHighPct)} dy={-3} fontSize={fs} fill="var(--accent)" textAnchor="start">
                Suggested full {suggestedHighPct}%
              </text>
            </>
          )}
          {suggestedLowPct !== undefined && suggestedLowPct >= mMin && suggestedLowPct <= mMax && (
            <>
              <line x1={PAD_L} x2={VB_W - PAD_R} y1={yForM(suggestedLowPct)} y2={yForM(suggestedLowPct)} stroke="var(--accent)" strokeWidth={1.5} strokeDasharray="2 3" />
              <text x={PAD_L} y={yForM(suggestedLowPct)} dy={-3} fontSize={fs} fill="var(--accent)" textAnchor="start">
                Suggested water at {suggestedLowPct}%
              </text>
            </>
          )}

          {moisturePath && (
            <path d={moisturePath} fill="none" stroke="var(--viz-sensor)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          )}
          {moistureEnd && (
            <>
              <circle cx={xForFrac(moistureEnd.day, moistureEnd.frac)} cy={yForM(moistureEnd.pct)} r={4} fill="var(--viz-sensor)" stroke="var(--panel)" strokeWidth={2} />
              <text x={xForFrac(moistureEnd.day, moistureEnd.frac) - 6} y={yForM(moistureEnd.pct) - 6} fontSize={fs} fill="var(--muted)" textAnchor="end">
                Sensor
              </text>
            </>
          )}

          {/* Detected field-capacity plateaus. */}
          {plateauPoints.map((p, i) => (
            <circle key={i} cx={xForFrac(p.day, p.frac)} cy={yForM(p.pct)} r={3.5} fill="var(--accent)" stroke="var(--panel)" strokeWidth={1.5} />
          ))}

          {hoverLine}
          {overlay}
        </svg>
      ) : (
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          role="img"
          aria-label={`Soil history for ${zone.name}`}
          style={{ touchAction: 'none' }}
        >
          {gridVals.map((v) => {
            const y = yFor(v);
            return (
              <g key={v}>
                <line x1={PAD_L} x2={VB_W - PAD_R} y1={y} y2={y} stroke="var(--border)" strokeWidth={1} />
                <text x={PAD_L - 4} y={y} dy={3} fontSize={fs} fill="var(--faint)" textAnchor="end">
                  {v.toFixed(2)}
                </text>
              </g>
            );
          })}

          {days.map((date, i) =>
            i % 5 === 0 ? (
              <text key={date} x={xForDay(i)} y={VB_H - 6} fontSize={fs} fill="var(--faint)" textAnchor="middle">
                {monthDay(date)}
              </text>
            ) : null,
          )}

          {days.map((date, i) => {
            const center = xForDay(i);
            const r = rainByDate.get(date) ?? 0;
            const w = waterByDate.get(date) ?? 0;
            return (
              <g key={date}>
                {r > 0 && (
                  <rect x={center - barGap / 2 - barW} y={yFor(r)} width={barW} height={baseY - yFor(r)} rx={2} fill="var(--viz-rain)" />
                )}
                {w > 0 && <rect x={center + barGap / 2} y={yFor(w)} width={barW} height={baseY - yFor(w)} rx={2} fill="var(--viz-water)" />}
              </g>
            );
          })}

          <line x1={PAD_L} x2={VB_W - PAD_R} y1={triggerY} y2={triggerY} stroke="var(--muted)" strokeWidth={1} opacity={0.6} />
          <text x={VB_W - PAD_R} y={triggerY} dy={-3} fontSize={fs} fill="var(--muted)" textAnchor="end">
            waters at {trigger.toFixed(2)} in
          </text>

          {modelPath && (
            <path d={modelPath} fill="none" stroke="var(--viz-model)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          )}
          {modelEndIdx >= 0 && (
            <>
              <circle cx={xForDay(modelEndIdx)} cy={modelEndY} r={4} fill="var(--viz-model)" stroke="var(--panel)" strokeWidth={2} />
              <text x={xForDay(modelEndIdx) - 6} y={modelLabelY} fontSize={fs} fill="var(--muted)" textAnchor="end">
                Model
              </text>
            </>
          )}

          {sensorPath && (
            <path d={sensorPath} fill="none" stroke="var(--viz-sensor)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          )}
          {sensorEnd && (
            <>
              <circle cx={xForFrac(sensorEnd.day, sensorEnd.frac)} cy={yFor(sensorEnd.deficit)} r={4} fill="var(--viz-sensor)" stroke="var(--panel)" strokeWidth={2} />
              <text x={xForFrac(sensorEnd.day, sensorEnd.frac) - 6} y={sensorLabelY} fontSize={fs} fill="var(--muted)" textAnchor="end">
                Sensor
              </text>
            </>
          )}

          {hoverLine}
          {overlay}
        </svg>
      )}

      {hover && effMode !== 'table' && (
        <div className="viz-tooltip" style={{ left: hover.left, top: hover.top }}>
          <div className="when">{friendlyDate(days[hover.day]!)}</div>
          {tooltipRows(hover.day).length === 0 ? (
            <div className="row">
              <span className="name">No data</span>
            </div>
          ) : (
            tooltipRows(hover.day).map((row) => (
              <div className="row" key={row.key}>
                <span className="name">
                  <Swatch kind={row.kind} color={row.color} /> {row.name}
                </span>
                <span className="val">{row.val}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
